import {VFC} from 'react';
import gql from 'graphql-tag';
import {type QueryHookOptions, useQuery} from 'services/apollo';
import {useParams, useHistory} from 'react-router-dom';
import {DriverAssignment} from 'services/graphql';
import {formatOrderNumber} from 'util/StringUtils';
import LoadingSpinner from 'components/LoadingSpinner';
import NotFound from 'components/NotFound';
import {EzAlert, EzBlankState, EzLayout, EzLink} from '@ezcater/recipe';
import PhoneNumbers from 'constants/PhoneNumbers';
import {FlashMessages} from '@ezcater/flash-messages';
import {TRACK_DRIVER_ASSIGNMENT_PAGE_NAME} from 'util/tracking/trackingEvents';
import {useDriverPageViewTracking} from 'util/tracking';
import {useTranslation} from 'react-i18next';
import {DELIVERIES_PATH} from 'paths';
import {useCurrentAccount} from 'components/CurrentAccount';
import {useFederationApiEppoFlag} from 'services/eppo';
import {getAdaptedAssignmentQuery} from 'services/driverApiAdapters';
import {
  DmDeliveryStatus,
  type DriverAssignmentQuery,
  type DriverAssignmentQueryVariables,
  type DriverProgressAssignmentQuery,
  type DriverProgressAssignmentQueryVariables,
} from 'services/graphql/types';
import FeedbackForm from 'components/FeedbackForm';
import {StickyFooter} from 'components/FeedbackForm/FeedbackForm.styles';
import Layout from '../Layout';
import {
  ContentsContainer,
  ScrollBarContainer,
  ScrollBarContent,
  SummaryBarTitleContainer,
  DeliveryCompletedAlertWrapper,
} from './DriverProgress.styles';
import ActionButton from './ActionButton';
import StartDelivery from './StartDelivery';
import InProgress from './InProgress';

export const AssignmentQuery = gql(/* GraphQL */ `
  query DriverProgressAssignmentQuery($id: ID!) {
    me {
      driverAccount {
        assignment(id: $id) {
          deliveryId
          deliverySetup
          distanceInMiles
          driverAskFor
          dropoffAddress {
            city
            id
            instructions
            locationName
            phone
            state
            street1
            street2
            zip
          }
          dropoffWindow {
            endAt
            startAt
          }
          durationInMinutes
          eventAt
          feedback {
            message
          }
          formattedOrderNumber
          id
          orderItems {
            name
            quantity
          }
          pickupAddress {
            city
            id
            instructions
            locationName
            state
            street1
            street2
            zip
          }
          status
        }
        id
      }
    }
  }
`);

type UseAssignmentsQueryOptions = Required<
  Pick<
    QueryHookOptions<DriverAssignmentQuery, DriverAssignmentQueryVariables>,
    'onCompleted' | 'variables'
  >
>;

type UseAssignmentsQueryResult = Pick<
  ReturnType<typeof useQuery<DriverAssignmentQuery, DriverAssignmentQueryVariables>>,
  'data' | 'error' | 'loading'
>;

const useAssignmentQuery = (options: UseAssignmentsQueryOptions): UseAssignmentsQueryResult => {
  const featureFlag = useFederationApiEppoFlag();
  const assignmentQuery = useQuery<
    DriverProgressAssignmentQuery,
    DriverProgressAssignmentQueryVariables
  >(AssignmentQuery, {
    ...options,
    onCompleted: data => {
      // Feature flag is checked here because Apollo will still trigger
      // `onCompleted` callback even with option `skip: true`.
      if (!featureFlag) return;

      const adaptedData = getAdaptedAssignmentQuery(data);

      if (adaptedData) {
        options.onCompleted(adaptedData);
      }
    },
    skip: !featureFlag,
  });
  const legacyAssignmentQuery = useQuery<DriverAssignmentQuery, DriverAssignmentQueryVariables>(
    DriverAssignment,
    {
      ...options,
      onCompleted: queryResultData => {
        // Feature flag is checked here because Apollo will still trigger
        // `onCompleted` callback even with option `skip: true`.
        if (!featureFlag) {
          options.onCompleted(queryResultData);
        }
      },
      skip: featureFlag,
    }
  );

  if (featureFlag) {
    return {
      data: getAdaptedAssignmentQuery(assignmentQuery.data),
      error: assignmentQuery.error,
      loading: assignmentQuery.loading,
    };
  }

  return legacyAssignmentQuery;
};

const NotFoundPage: VFC = () => {
  const {t} = useTranslation();
  return (
    <NotFound title={t('driverProgress.notFound.title')}>
      {t('driverProgress.notFound.message')}
      <EzLink href={`tel:${PhoneNumbers.EZDISPATCH_DIRECT}`}>
        {PhoneNumbers.EZDISPATCH_DIRECT}
      </EzLink>
      .
    </NotFound>
  );
};

const DriverProgress = () => {
  const {t} = useTranslation();
  const history = useHistory();
  const {driverAssignmentId} = useParams<{driverAssignmentId: string}>();
  const currentAccount = useCurrentAccount();
  const currentAccountId = currentAccount.id || undefined;
  const {data, loading, error} = useAssignmentQuery({
    variables: {id: driverAssignmentId},
    onCompleted: queryResultData => {
      const status = queryResultData?.driverAssignment.delivery.status;

      if (status === DmDeliveryStatus.Expired) {
        history.replace(DELIVERIES_PATH);
      }
    },
  });
  const assignment = data?.driverAssignment;

  useDriverPageViewTracking(
    TRACK_DRIVER_ASSIGNMENT_PAGE_NAME,
    assignment?.delivery?.id,
    currentAccountId
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !assignment) {
    return <NotFoundPage />;
  }

  const orderNumber = formatOrderNumber(assignment.delivery.orderNumber);
  const deliveryStatus = assignment.delivery.status || DmDeliveryStatus.Assigned;

  return (
    <Layout title={`${orderNumber} - ezCater Delivery`} orderNumber={orderNumber}>
      <ContentsContainer>
        <FlashMessages />
        <ScrollBarContainer>
          {orderNumber && (
            <SummaryBarTitleContainer>
              {t('driverProgress.header.orderNumber', {orderNumber})}
            </SummaryBarTitleContainer>
          )}
          <ScrollBarContent>
            {deliveryStatus === DmDeliveryStatus.Assigned && <StartDelivery data={data} />}
            {deliveryStatus === DmDeliveryStatus.InProgress && <InProgress data={data} />}
            {deliveryStatus === DmDeliveryStatus.Completed && (
              <EzLayout layout="stack">
                <DeliveryCompletedAlertWrapper>
                  <EzAlert use="success" headline="Delivery Completed" />
                </DeliveryCompletedAlertWrapper>
                <FeedbackForm driverAssignmentId={driverAssignmentId} />
              </EzLayout>
            )}
            {!!assignment.feedback && (
              <EzLayout layout="stack">
                <EzBlankState title="Feedback received" message="Thank you for your feedback" />
              </EzLayout>
            )}
          </ScrollBarContent>
          <StickyFooter>
            <ActionButton
              eventAt={assignment.delivery.eventAt}
              driverAssignmentId={driverAssignmentId}
              status={deliveryStatus}
            />
          </StickyFooter>
        </ScrollBarContainer>
      </ContentsContainer>
    </Layout>
  );
};

export default DriverProgress;
